import type { Dimension as TmDimension, MapData, Topic as TmTopic } from "@serendipityai/tm-visual";
import { useCallback, useEffect } from "react";
import { styled } from "styled-components";

import { useLazyGetDimensionQuery } from "api/dimensionApi.slice";
import { PanelContainer } from "common/components";
import { navbarComplementHeight } from "common/constants/navbarHeight";
import { useQueryParams } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { addDimensionToMapSuccess, removeTopicFromMap } from "domains/createMapDetail/actions";
import { KeyIssuePanel } from "domains/createMapDetail/components/keyIssueList/KeyIssuePanel";
import {
  getCurrentMapDimensionIds,
  getExistingDimensionIds,
  getIsKeyIssuesDragging,
  getLocalImage,
  getNewCustomDimensions,
} from "domains/createMapDetail/selectors";
import { dimensionSlice } from "domains/dimension/dimension.slice";
import { useDimensions } from "domains/dimension/useDimensions";
import { useTrackDimensions } from "domains/dimension/useTrackDimensions";
import { activateModal } from "domains/modal/actions";
import { TMCopyrightText } from "domains/topicDetail/components/TMCopyrightText";
import { TopicDetailPanel } from "domains/topicDetail/components/TopicDetailPanel";
import { TopicDetailTopBar } from "domains/topicDetail/components/TopicDetailTopBar";
import { setTransformationMapInnerNode, updateTransformationMapTopic } from "domains/transformationMap/actions";
import { TransformationMap } from "domains/transformationMap/components/TransformationMap";
import {
  getIsTmPending,
  getTmEditMode,
  getTmSelectedInnerNode,
  getTmSelectedOuterNode,
  getTmTopic,
} from "domains/transformationMap/selectors";

const MapContainer = styled.div`
  height: ${navbarComplementHeight};
  position: relative;
  width: 100%;
`;

const TextContainer = styled.div`
  bottom: 2.5rem;
  left: 1rem;
  position: absolute;
`;

type TopicDetailProps = { isNew: boolean };

export const TopicDetail = ({ isNew }: TopicDetailProps) => {
  const dispatch = useDispatch();
  const [fetchDimension] = useLazyGetDimensionQuery();
  const { editing } = useQueryParams();
  const dimensionIds = useSelector(getCurrentMapDimensionIds);
  const existingDimensionIds = useSelector(getExistingDimensionIds);
  const isDraggingTopic = useSelector(getIsKeyIssuesDragging);
  const isPending = useSelector(getIsTmPending);
  const localImage = useSelector(getLocalImage);
  const newCustomDimensions = useSelector(getNewCustomDimensions);
  const isEditing = useSelector(getTmEditMode);
  const selectedInnerNode = useSelector(getTmSelectedInnerNode);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const topic = useSelector(getTmTopic);
  const { trackAddDimension, trackRemoveDimension } = useTrackDimensions();

  const existingDimensions = useDimensions(existingDimensionIds);

  const dimensions = dimensionIds.reduce<TmDimension[]>((accumulator, next) => {
    const existingDimension = existingDimensions.find(({ dimension }) => dimension.id === next);
    if (existingDimension !== undefined)
      return [...accumulator, { ...existingDimension.dimension, topics: existingDimension.topics }];
    const newDimension = newCustomDimensions.find((dimension) => dimension.id === next);
    return newDimension === undefined ? accumulator : [...accumulator, newDimension];
  }, []);

  useEffect(() => {
    dispatch(dimensionSlice.actions.setSearch(""));
  }, [dispatch]);

  const deselectKeyIssue = useCallback(() => dispatch(setTransformationMapInnerNode({ id: undefined })), [dispatch]);

  const handleSelectImage = useCallback(() => {
    if (isEditing) dispatch(activateModal({ id: "MapImagePicker" }));
    else deselectKeyIssue();
  }, [deselectKeyIssue, dispatch, isEditing]);

  const handleSelectInnerNode = (topic: TmTopic) => dispatch(setTransformationMapInnerNode({ id: topic.id }));

  const handleSelectedOuterNode = useCallback(
    (topic: TmTopic) => dispatch(updateTransformationMapTopic({ id: topic.id })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOuterNode],
  );

  const handlePanelVisibilityToggle = () => window.dispatchEvent(new Event("resize"));

  const handleDragDrop = (keyIssueId: string) =>
    void fetchDimension({ id: keyIssueId }, true).then((result) => {
      if (!result.isSuccess) return;
      dispatch(addDimensionToMapSuccess(result.data.dimension.id));
      trackAddDimension(result.data.dimension.id);
    });

  const handleRemoveInnerNode = (topic: TmTopic) => {
    dispatch(removeTopicFromMap({ keyIssueId: topic.id }));
    trackRemoveDimension(topic.id);
  };

  const image = isEditing && localImage !== undefined ? localImage : topic?.images?.thumbnail;

  const tmTopic: MapData | undefined =
    topic === undefined
      ? undefined
      : {
          dimensions:
            topic.dimensions?.map((dimension) => ({
              id: dimension.id,
              image_url: dimension.images?.thumbnail,
              name: dimension.name,
              topics: dimension.topics ?? [],
              is_custom_dimension: dimension.is_custom_dimension,
            })) ?? [],
          id: topic.id,
          image_url: topic.images?.thumbnail,
          name: topic.name,
        };

  const creator = topic?.is_custom ? topic.provider?.name : undefined;

  return (
    <PanelContainer
      onPanelVisibilityToggle={handlePanelVisibilityToggle}
      panelLeft={editing || isNew ? <KeyIssuePanel isNewMap={isNew} /> : null}
      panelRight={<TopicDetailPanel topicId={topic?.id} />}
    >
      <MapContainer>
        <TransformationMap
          dimensions={dimensions}
          handleDragDrop={handleDragDrop}
          handleRemoveInnerNode={handleRemoveInnerNode}
          handleSelectedOuterNode={handleSelectedOuterNode}
          handleSelectImage={handleSelectImage}
          handleSelectInnerNode={handleSelectInnerNode}
          handleDeselectInnerNode={deselectKeyIssue}
          image={image}
          isDraggingTopic={isDraggingTopic}
          isEditing={isEditing}
          isLoading={isPending}
          selectedInnerNode={selectedInnerNode}
          selectedOuterNode={selectedOuterNode}
          topic={tmTopic}
        />
        <TopicDetailTopBar />
        <TextContainer>{!isEditing && <TMCopyrightText topic={topic} creator={creator} />}</TextContainer>
      </MapContainer>
    </PanelContainer>
  );
};
